import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

export type ScrollSyncRegister = (element: HTMLElement | null) => () => void;

const ScrollSyncContext = createContext<ScrollSyncRegister | null>(null);

export const ScrollSyncProvider = ScrollSyncContext.Provider;

export const useScrollSyncRegister = (): ScrollSyncRegister | null =>
  useContext(ScrollSyncContext);

const SCROLL_EDGE_TOLERANCE = 1;

const updateScrollState = (element: HTMLElement): void => {
  const canLeft = element.scrollLeft > SCROLL_EDGE_TOLERANCE;
  const canRight =
    element.scrollWidth - element.clientWidth - element.scrollLeft >
    SCROLL_EDGE_TOLERANCE;
  element.dataset.canScrollLeft = canLeft ? "true" : "false";
  element.dataset.canScrollRight = canRight ? "true" : "false";
};

export const useScrollSyncGroup = (): { register: ScrollSyncRegister } => {
  const elementsRef = useRef<Set<HTMLElement>>(new Set());
  const listenersRef = useRef<Map<HTMLElement, () => void>>(new Map());
  const observersRef = useRef<Map<HTMLElement, ResizeObserver>>(new Map());

  const register = useCallback<ScrollSyncRegister>((element) => {
    if (element === null) return () => {};
    const elements = elementsRef.current;
    const listeners = listenersRef.current;
    const observers = observersRef.current;
    if (elements.has(element)) return () => {};

    const peer = Array.from(elements).find((el) => el !== element);
    if (peer && element.scrollLeft !== peer.scrollLeft) {
      element.scrollLeft = peer.scrollLeft;
    }
    elements.add(element);
    updateScrollState(element);

    const handler = (): void => {
      const { scrollLeft } = element;
      let changed = false;
      elements.forEach((other) => {
        if (other !== element && other.scrollLeft !== scrollLeft) {
          other.scrollLeft = scrollLeft;
          changed = true;
        }
      });
      if (changed) {
        elements.forEach(updateScrollState);
      } else {
        updateScrollState(element);
      }
    };
    element.addEventListener("scroll", handler, { passive: true });
    listeners.set(element, handler);

    const observer = new ResizeObserver(() => {
      updateScrollState(element);
    });
    observer.observe(element);
    observers.set(element, observer);

    return (): void => {
      const storedHandler = listeners.get(element);
      if (storedHandler) {
        element.removeEventListener("scroll", storedHandler);
        listeners.delete(element);
      }
      const storedObserver = observers.get(element);
      if (storedObserver) {
        storedObserver.disconnect();
        observers.delete(element);
      }
      elements.delete(element);
    };
  }, []);

  return useMemo(() => ({ register }), [register]);
};
